import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/src/shadComponents/ui/dialog';
import { CheckCircle, Search } from 'lucide-react';

interface Org {
    id: number;
    name: string;
    logo?: string;
}

interface OrgSwitcherDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    orgs: Org[];
    currentOrgId: number;
    onSelect: (org: Org) => void;
}

export default function OrgSwitcherDialog({
    open,
    onOpenChange,
    orgs,
    currentOrgId,
    onSelect,
}: OrgSwitcherDialogProps) {
    const [search, setSearch] = useState('');

    const filtered = orgs.filter((org) =>
        org.name.toLowerCase().includes(search.toLowerCase()),
    );

    const handleSelect = (org: Org) => {
        onSelect(org);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                onOpenChange(v);
                if (!v) setSearch('');
            }}
        >
            <DialogContent className="max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Switch Organization</DialogTitle>
                    <DialogDescription>
                        Select an organization to switch to.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2 rounded-md border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                    <Search className="size-4 shrink-0 text-muted-foreground" />
                    <input
                        placeholder="Search organizations..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        autoFocus
                    />
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 max-h-[400px] border rounded-md">
                    {filtered.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            No organizations match your search.
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {filtered.map((org) => (
                                <button
                                    key={org.id}
                                    onClick={() => handleSelect(org)}
                                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 cursor-pointer"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {org.name}
                                        </p>
                                    </div>
                                    {org.id === currentOrgId && (
                                        <CheckCircle className="size-4 shrink-0 text-primary" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
